import {
  ApostleAttachAction,
  ApostleAttachActionType,
  ApostleLite,
  AttachApostle
} from '@apeiron/apostle'
import { AgeToSlotCount, useFrontendConfigContext } from '@apeiron/config'
import {
  CONTRACT,
  ContractEventParams,
  useApproveErc721,
  useContract
} from '@apeiron/contract'
import {
  PlanetApostlePayload,
  PlanetLite,
  PlanetMiddleLite
} from '@apeiron/planet'
import {
  createStrictContext,
  MayNull,
  useAccountContext,
  useChainSwitcher,
  useMessageContext
} from '@apeiron/temp'
import { ApeironPleaseWait } from '@apeiron/ui'
import { useMktErrorHandling } from '@apeiron/wallet'
import useGetMyPlanetsMiddleLite from '@src/hooks/graphql/useGetMyPlanetsMiddleLite'
import useGetMySeedPlanetsMiddleLite from '@src/hooks/graphql/useGetMySeedPlanetsMiddleLite'
import useSignManagePlanetApostle from '@src/hooks/graphql/useSignManagePlanetApostle'
import * as R from 'ramda'
import { ReactNode, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Address } from 'viem'

const [ContextProvider, useApostleAttachContext] =
  createStrictContext<ManageApostleContext>('ApostleAttach')

export { useApostleAttachContext }

export const ApostleAttachProvider = (props: Props) => {
  const { children } = props

  const { config } = useFrontendConfigContext()

  const { fetch: fetchNftPlanets } = useGetMyPlanetsMiddleLite()

  const { fetch: fetchSeedPlanets } = useGetMySeedPlanetsMiddleLite()

  const { mutate: signMessage } = useSignManagePlanetApostle()

  const { sendWithOption: doSave } = useContract(
    CONTRACT.APOSTLE.MANAGE_PLANET_APOSTLES
  )

  const { getContractAddress } = useChainSwitcher()

  const { account } = useAccountContext()

  const approveUseApostle = useApproveErc721(
    account.walletAddress as Address,
    getContractAddress(CONTRACT.APOSTLE.GET_USER_ALLOWANCE.address)
  )

  const { t } = useTranslation()

  const { setInfo } = useMessageContext()

  const { showErrorPopup } = useMktErrorHandling()

  const [selectedPlanets, setSelectedPlanets] = useState<PlanetMiddleLite[]>([])

  const [originalAttachTable, setOriginalAttachTable] =
    useState<ApostleAttachTable>({})

  const [displayAttachTable, setDisplayAttachTable] =
    useState<ApostleAttachTable>({})

  const buildAttachTable = useCallback(
    (planetGroup: PlanetGroup): ApostleAttachTable => {
      const { nfts, seeds } = planetGroup

      const planets = R.concat(nfts, seeds)

      return R.reduce(
        (acc: ApostleAttachTable, planet: PlanetMiddleLite) => {
          const attachedApostles: MayNull<AttachApostle>[] = R.map(
            (apostle: MayNull<ApostleLite>) => {
              return R.isNotNil(apostle)
                ? R.assoc('materialize', false, apostle)
                : null
            },
            R.pluck('apostle', planet.relicApostles || [])
          )

          const emptyApostles: MayNull<AttachApostle>[] = R.repeat(
            null,
            planet.relicApostleLimit - R.length(attachedApostles)
          )

          return R.assoc(
            planet.planetID,
            R.concat(attachedApostles, emptyApostles),
            acc
          )
        },
        {},
        planets
      )
    },
    []
  )

  const fetchPlanetGroup = useCallback(
    async (planets: PlanetLite[]): Promise<PlanetGroup> => {
      const seeds = R.filter(
        (planet: PlanetLite) => R.equals(true, planet.seed),
        planets
      )

      const nfts = R.filter(
        (planet: PlanetLite) => R.equals(false, planet.seed),
        planets
      )

      const { data: fetchedNfts } = R.isNotEmpty(nfts)
        ? await fetchNftPlanets({
            filterInput: { planetID: R.pluck('planetID', nfts) }
          })
        : { data: [] }

      const { data: fetchedSeeds } = R.isNotEmpty(seeds)
        ? await fetchSeedPlanets({
            planetIds: R.pluck('planetID', seeds)
          })
        : { data: [] }

      return {
        nfts: fetchedNfts,
        seeds: fetchedSeeds
      }
    },
    [fetchNftPlanets, fetchSeedPlanets]
  )

  const sortPlanets = useCallback(
    (order: PlanetLite[], lookup: PlanetGroup) => {
      const { nfts, seeds } = lookup

      const sort = R.pipe<any, MayNull<PlanetMiddleLite>[], PlanetMiddleLite[]>(
        R.map((planet: PlanetLite) => {
          const nft = R.find(R.propEq(planet.planetID, 'planetID'), nfts)

          const seed = R.find(R.propEq(planet.planetID, 'planetID'), seeds)

          return nft || seed
        }),
        R.reject(R.isNil)
      )

      return sort(order)
    },
    []
  )

  const toggleApostleAsMaterial = useCallback(
    (planetId: string, index: number) => {
      const materialize = R.pathOr(
        false,
        [planetId, index, 'materialize'],
        displayAttachTable
      )

      const newTable = R.assocPath(
        [planetId, index, 'materialize'],
        !materialize,
        displayAttachTable
      )

      setDisplayAttachTable(newTable)
    },
    [displayAttachTable]
  )

  const checkAnyUpdate = useCallback(() => {
    const comparator = (a: ApostleLite, b: ApostleLite) => a.id === b.id

    const extractApostles = R.pipe<
      any,
      MayNull<ApostleLite>[][],
      MayNull<ApostleLite>[],
      ApostleLite[]
    >(R.values, R.flatten, R.reject(R.isNil))

    const allDisplayApostles = extractApostles(displayAttachTable)

    const allOriginalApostles = extractApostles(originalAttachTable)

    const addedApostles = R.differenceWith(
      comparator,
      allDisplayApostles,
      allOriginalApostles
    )

    const removedApostles = R.differenceWith(
      comparator,
      allOriginalApostles,
      allDisplayApostles
    )

    return R.isNotEmpty([...addedApostles, ...removedApostles])
  }, [displayAttachTable, originalAttachTable])

  const checkIsNew = useCallback(
    (planetId: MayNull<string>, apostleId: MayNull<string>) => {
      if (R.isNotNil(planetId) && R.isNotNil(apostleId)) {
        const getIds = R.pipe(R.reject(R.isNil), R.pluck('id'))

        const ids = getIds(originalAttachTable[planetId] || [])

        return !R.includes(apostleId, ids)
      }

      return false
    },
    [originalAttachTable]
  )

  const countAllowedSelection = useCallback(
    (planetId: string) => {
      const planet = R.find(R.propEq(planetId, 'planetID'), selectedPlanets)

      const getApostleIds = R.pipe(
        R.values,
        R.flatten,
        R.reject(R.isNil),
        R.pluck('id')
      )

      const getApostleIdsInPlanet = R.pipe(R.reject(R.isNil), R.pluck('id'))

      const displayIds = getApostleIds(displayAttachTable)

      const originalIds = getApostleIds(originalAttachTable)

      const occupiedCount = R.length(
        getApostleIdsInPlanet(displayAttachTable[planetId])
      )

      const addedCount = R.length(R.difference(displayIds, originalIds))

      const removedCount = R.length(R.difference(originalIds, displayIds))

      const mutatedCount = addedCount + removedCount

      const occupiedLimit = planet?.relicApostleLimit || 0

      const mutatedLimit = config?.managePlanetApostle.maxManageCount || 0

      const limitedCount = Math.min(
        occupiedLimit - occupiedCount,
        mutatedLimit - mutatedCount
      )

      const allowedCount = Math.max(limitedCount, 0)

      return allowedCount
    },
    [
      config?.managePlanetApostle.maxManageCount,
      displayAttachTable,
      originalAttachTable,
      selectedPlanets
    ]
  )

  const addApostles = useCallback(
    (planetId: string, newAttachApostles: AttachApostle[]) => {
      const planet = R.find(R.propEq(planetId, 'planetID'), selectedPlanets)

      if (R.isNotNil(planet)) {
        const oldAttachApostles = R.reject(
          R.isNil,
          displayAttachTable[planetId] || []
        )

        const attachApostles = R.concat(oldAttachApostles, newAttachApostles)

        const emptyApostles: MayNull<AttachApostle>[] = R.repeat(
          null,
          (planet?.relicApostleLimit || 0) - R.length(attachApostles)
        )

        const attachTable = R.assoc(
          planet.planetID,
          R.concat(attachApostles as MayNull<AttachApostle>[], emptyApostles),
          displayAttachTable
        )

        setDisplayAttachTable(attachTable)
      }
    },
    [displayAttachTable, selectedPlanets]
  )

  const replaceApostle = useCallback(
    (planetId: string, apostle: ApostleLite, index: number) => {
      setDisplayAttachTable(
        R.assocPath([planetId, index], apostle, displayAttachTable)
      )
    },
    [displayAttachTable]
  )

  const replacePlanet = useCallback(
    async (planet: PlanetLite, index: number) => {
      const planetGroup = await fetchPlanetGroup([planet])

      const removePlanet = selectedPlanets[index]

      const addPlanet = R.head(planetGroup.nfts) || R.head(planetGroup.seeds)

      if (R.isNotNil(removePlanet) && R.isNotNil(addPlanet)) {
        const attachTable = buildAttachTable(planetGroup)

        const replaceAttachTable = R.pipe<
          any,
          ApostleAttachTable,
          ApostleAttachTable
        >(R.dissoc(removePlanet.planetID), R.mergeDeepLeft(attachTable))

        setSelectedPlanets(R.assoc(index, addPlanet, selectedPlanets))
        setOriginalAttachTable(replaceAttachTable(originalAttachTable))
        setDisplayAttachTable(replaceAttachTable(displayAttachTable))
      }
    },
    [
      buildAttachTable,
      fetchPlanetGroup,
      displayAttachTable,
      selectedPlanets,
      originalAttachTable
    ]
  )

  const removeApostle = useCallback(
    (planetId: string, index: number) => {
      const planet = R.find(R.propEq(planetId, 'planetID'), selectedPlanets)

      if (R.isNotNil(planet)) {
        const oldAttachApostles = R.reject(
          R.isNil,
          displayAttachTable[planetId] || []
        )

        const attachApostles = R.remove(index, 1, oldAttachApostles)

        const emptyApostles: MayNull<ApostleLite>[] = R.repeat(
          null,
          (planet?.relicApostleLimit || 0) - R.length(attachApostles)
        )

        const attachTable = R.assoc(
          planet.planetID,
          R.concat(attachApostles as MayNull<ApostleLite>[], emptyApostles),
          displayAttachTable
        )

        setDisplayAttachTable(attachTable)
      }
    },
    [displayAttachTable, selectedPlanets]
  )

  const resetApostle = useCallback(() => {
    setDisplayAttachTable(originalAttachTable)
  }, [originalAttachTable])

  const updatePlanets = useCallback(
    async (planets: PlanetLite[]) => {
      const newPlanets = R.difference(planets, selectedPlanets)

      const planetGroup = await fetchPlanetGroup(newPlanets)

      const sortedPlanets = sortPlanets(newPlanets, planetGroup)

      setSelectedPlanets(R.concat(selectedPlanets, sortedPlanets))

      const attachTable = buildAttachTable(planetGroup)

      setOriginalAttachTable(R.mergeDeepLeft(attachTable, originalAttachTable))
      setDisplayAttachTable(R.mergeDeepLeft(attachTable, displayAttachTable))
    },
    [
      displayAttachTable,
      selectedPlanets,
      originalAttachTable,
      buildAttachTable,
      fetchPlanetGroup,
      sortPlanets
    ]
  )

  const refreshAfterSave = useCallback(async () => {
    const planetGroup = await fetchPlanetGroup(selectedPlanets)

    const attachTable = buildAttachTable(planetGroup)

    setOriginalAttachTable(R.mergeDeepLeft(attachTable, originalAttachTable))
    setDisplayAttachTable(R.mergeDeepLeft(attachTable, displayAttachTable))
  }, [
    displayAttachTable,
    selectedPlanets,
    originalAttachTable,
    buildAttachTable,
    fetchPlanetGroup
  ])

  const removePlanet = useCallback(
    (planetId: string) => {
      setOriginalAttachTable(R.dissoc(planetId, originalAttachTable))
      setDisplayAttachTable(R.dissoc(planetId, displayAttachTable))
      setSelectedPlanets(
        R.reject(R.propEq(planetId, 'planetID'), selectedPlanets)
      )
    },
    [displayAttachTable, originalAttachTable, selectedPlanets]
  )

  const saveAttachment = useCallback(async () => {
    try {
      setInfo({
        message: t('common.wait_token_approve')
      })

      const attachAddress = getContractAddress(
        CONTRACT.APOSTLE.MANAGE_PLANET_APOSTLES.address
      )

      await approveUseApostle(attachAddress)

      const planetIds = R.keys(displayAttachTable)

      const apiInput = R.map(planetId => {
        const actions = [] as ApostleAttachActionType[]

        const originalApostles = R.reject(
          R.isNil,
          originalAttachTable[planetId] || []
        )

        const originalIds = R.pluck('id', originalApostles)

        const displayApostles = R.reject(
          R.isNil,
          displayAttachTable[planetId] || []
        )

        const materilizeApostles = R.filter(
          (apostle: AttachApostle) => apostle.materialize,
          displayApostles
        )

        const materializeIds = R.pluck('id', materilizeApostles)

        const displayIds = R.pluck('id', displayApostles)

        const resultIds = R.reject(
          (id: string) => R.includes(id, materializeIds),
          displayIds
        )

        const addedIds = R.difference(resultIds, originalIds)

        const removedIds = R.difference(originalIds, resultIds)

        R.forEach(id => {
          actions.push({
            action: ApostleAttachAction.Burn,
            apostleID: id
          })
        }, addedIds)

        R.forEach(id => {
          actions.push({
            action: ApostleAttachAction.Mint,
            apostleID: id
          })
        }, removedIds)

        R.forEach(id => {
          actions.push({
            action: ApostleAttachAction.BurnAsReservedApostle,
            apostleID: id
          })
        }, materializeIds)

        if (R.isEmpty(actions)) {
          return null
        } else {
          return {
            planetID: planetId,
            relicApostleManagementActions: actions,
            slotWithApostleID: resultIds
          }
        }
      }, planetIds)

      const signature = await signMessage(R.reject(R.isNil, apiInput))

      const formatedPayload = R.map((payload: PlanetApostlePayload) => {
        return [
          payload.planetId,
          payload.seedPlanetId,
          payload.slotsWithApostlesIds,
          payload.apostleIds,
          payload.genes,
          payload.iVs,
          payload.createTimes,
          payload.actions
        ]
      }, signature?.managementPayloads || [])

      setInfo({
        message: (
          <ApeironPleaseWait
            subject={t('sanctuary.manage_apostle.attach_detaching')}
          />
        )
      })

      await doSave(
        { verifyEvent: ContractEventParams.ManagePlanetApostle },
        formatedPayload,
        signature?.timestamp,
        signature?.signature
      )
      await refreshAfterSave()

      setInfo({
        confirm: t('common.confirm'),
        message: t('sanctuary.manage_apostle.saved_success')
      })

      return true
    } catch (error) {
      showErrorPopup(error)

      return false
    }
  }, [
    displayAttachTable,
    originalAttachTable,
    approveUseApostle,
    doSave,
    getContractAddress,
    refreshAfterSave,
    setInfo,
    showErrorPopup,
    signMessage,
    t
  ])

  return (
    <ContextProvider
      value={{
        displayAttachTable,
        originalAttachTable,
        selectedPlanets,
        configApostleSlotTable: config?.planet.apostleSlotTable || [],
        addApostles,
        checkAnyUpdate,
        checkIsNew,
        countAllowedSelection,
        removeApostle,
        removePlanet,
        replaceApostle,
        replacePlanet,
        resetApostle,
        saveAttachment,
        toggleApostleAsMaterial,
        updatePlanets
      }}
    >
      {children}
    </ContextProvider>
  )
}

type ApostleAttachTable = Record<string, AttachApostle[]>

type PlanetGroup = {
  nfts: PlanetMiddleLite[]
  seeds: PlanetMiddleLite[]
}

type ManageApostleContext = {
  displayAttachTable: ApostleAttachTable
  originalAttachTable: ApostleAttachTable
  configApostleSlotTable: AgeToSlotCount[]
  selectedPlanets: PlanetMiddleLite[]
  addApostles: (planetId: string, apostles: AttachApostle[]) => void
  checkAnyUpdate: () => boolean
  checkIsNew: (planetId: MayNull<string>, apostleId: MayNull<string>) => boolean
  countAllowedSelection: (planetId: string) => number
  removeApostle: (planetId: string, index: number) => void
  removePlanet: (planetId: string) => void
  replaceApostle: (
    planetId: string,
    apostle: ApostleLite,
    index: number
  ) => void
  replacePlanet: (planet: PlanetLite, index: number) => Promise<void>
  resetApostle: () => void
  saveAttachment: () => Promise<boolean>
  toggleApostleAsMaterial: (planetId: string, index: number) => void
  updatePlanets: (planets: PlanetLite[]) => void
}

type Props = {
  children: ReactNode
}
